import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { 
  FileInput, 
  FileOutput, 
  GitBranch, 
  Calculator, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  XCircle,
  Zap
} from 'lucide-react';
import './FlowNode.css';

interface FlowNodeData {
  stepId: string;
  stepNumber: number;
  title: string;
  description: string;
  type: string;
  nodeType: 'input' | 'processing' | 'output' | 'validation';
  status: 'pending' | 'running' | 'completed' | 'error';
  isHighlighted?: boolean;
  details?: {
    what: string;
    why: string;
    example: string;
  };
  codeLines?: [number, number];
}

export const FlowNode = memo<NodeProps<FlowNodeData>>(({ data, selected }) => {
  const getIcon = () => {
    switch (data.nodeType) {
      case 'input':
        return <FileInput size={16} />;
      case 'output':
        return <FileOutput size={16} />;
      case 'validation':
        return <CheckCircle size={16} />;
      case 'processing':
      default:
        return <Zap size={16} />;
    }
  };

  const getStatusIcon = () => {
    switch (data.status) {
      case 'running':
        return <Loader2 size={14} className="status-icon spinning" />;
      case 'completed':
        return <CheckCircle size={14} className="status-icon success" />;
      case 'error':
        return <XCircle size={14} className="status-icon error" />;
      default:
        return null;
    }
  };

  const getNodeClassName = () => {
    const classes = ['flow-node', `node-${data.nodeType}`, `status-${data.status}`];
    if (selected) classes.push('selected');
    if (data.isHighlighted) classes.push('highlighted');
    return classes.join(' ');
  };

  return (
    <div className={getNodeClassName()}>
      <Handle
        type="target"
        position={Position.Top}
        className="flow-handle handle-target"
      />
      
      <div className="node-header">
        <div className="node-number">{data.stepNumber}</div>
        <div className="node-icon">{getIcon()}</div>
        {data.status !== 'pending' && (
          <div className="node-status">{getStatusIcon()}</div>
        )}
      </div>
      
      <div className="node-content">
        <div className="node-title">{data.title}</div>
        <div className="node-description">{data.description}</div>
        
        {data.codeLines && (
          <div className="node-code-ref">
            Lines {data.codeLines[0]}-{data.codeLines[1]}
          </div>
        )}
      </div>
      
      <Handle
        type="source"
        position={Position.Bottom}
        className="flow-handle handle-source"
      />
    </div>
  );
});

FlowNode.displayName = 'FlowNode';